import type { RefObject } from 'react';
import { useEffect } from 'react';

import { useKeyboardShortcutsEnabled } from '../keyboard-shortcuts/KeyboardShortcutsContext';
import { isClaimBlockedByTileRegion, mayClaimArrowKeys } from './focus-regions';

/**
 * Document-level keyboard entry point for a composite widget (the board or a
 * table): calls `claim` when one of `keys` is pressed while nothing else owns
 * arrow keys — focus is loose (on <body>, after a lost restore) or parked on a
 * control without arrow behavior of its own, e.g. the navigation link that
 * keeps focus after switching pages via the menu. This is the only arming
 * mechanism: nothing steals focus on load; the first key enters the widget.
 */
export const useArrowKeyNet = (
    keys: ReadonlySet<string>,
    claim: (event: KeyboardEvent) => void,
    containerRef?: RefObject<HTMLElement | null>,
): void => {
    const keyboardShortcutsEnabled = useKeyboardShortcutsEnabled();

    useEffect(() => {
        if (!keyboardShortcutsEnabled) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent): void => {
            if (
                event.defaultPrevented ||
                event.isComposing ||
                // Alt/Ctrl/Meta arrow combos belong to the browser (history
                // navigation, scroll-to-edge); only Shift may combine.
                event.altKey ||
                event.ctrlKey ||
                event.metaKey ||
                !keys.has(event.key) ||
                !mayClaimArrowKeys(document.activeElement) ||
                isClaimBlockedByTileRegion(containerRef?.current ?? null)
            ) {
                return;
            }

            claim(event);
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [keys, claim, keyboardShortcutsEnabled, containerRef]);
};
